import { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Server,
  ShieldCheck,
  Database,
  CloudCog,
  Phone,
  Mail,
  Clock,
  CheckCircle,
  Menu,
  X,
  ArrowRight,
  Send,
  FileText,
  Award,
  Building2,
  Users,
  Zap,
  ChevronDown,
  Sun,
  Moon,
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Alhambra Logo Component - Corporate Style
const AlhambraLogo = ({ className = "h-10", theme = "light", onDark = false }) => (
  <svg className={className} viewBox="0 0 180 50" fill="none" xmlns="http://www.w3.org/2000/svg">
    <text x="0" y="35" fill={onDark ? "#ffffff" : (theme === "dark" ? "#C75BB5" : "#8B1E7B")} fontFamily="Poppins, sans-serif" fontWeight="600" fontSize="26" fontStyle="italic">
      alhambra
    </text>
    <text x="0" y="48" fill={onDark ? "#FF6B4A" : "#FF5733"} fontFamily="Open Sans, sans-serif" fontWeight="400" fontSize="10" fontStyle="italic">
      leave IT in our hands
    </text>
  </svg>
);

// Theme Toggle Component
const ThemeToggle = ({ theme, setTheme }) => {
  return (
    <button
      onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
      data-testid="theme-toggle"
      className="p-2 rounded-full bg-surface-highlight hover:bg-primary/10 transition-colors"
      aria-label="Toggle theme"
    >
      {theme === "dark" ? (
        <Sun size={20} className="text-secondary" />
      ) : (
        <Moon size={20} className="text-primary" />
      )}
    </button>
  );
};

// Navigation Component
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { name: "Services", href: "#services" },
    { name: "About", href: "#about" },
    { name: "Security", href: "#security" },
    { name: "Contact", href: "#contact" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "glass-nav py-3" : "bg-transparent py-5"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
        <a href="#" data-testid="logo-link">
          <AlhambraLogo className="h-12" onDark={!scrolled} />
        </a>

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-8">
          {navLinks.map((link) => (
            <a
              key={link.name}
              href={link.href}
              data-testid={`nav-${link.name.toLowerCase()}`}
              className={`font-medium transition-colors ${
                scrolled 
                  ? "text-text-muted hover:text-primary" 
                  : "text-white/80 hover:text-white"
              }`}
            >
              {link.name}
            </a>
          ))}
          <a
            href="#quote"
            data-testid="nav-get-quote"
            className="btn-primary px-6 py-2.5 font-medium"
          >
            Get A Quote
          </a>
        </div>

        {/* Mobile Menu Button */}
        <button
          className="md:hidden text-text-main"
          onClick={() => setMobileOpen(!mobileOpen)}
          data-testid="mobile-menu-toggle"
        >
          {mobileOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="md:hidden glass-nav"
          >
            <div className="px-6 py-4 flex flex-col gap-4">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  onClick={() => setMobileOpen(false)}
                  className="text-text-muted hover:text-text-main transition-colors py-2"
                >
                  {link.name}
                </a>
              ))}
              <a
                href="#quote"
                onClick={() => setMobileOpen(false)}
                className="btn-primary px-5 py-2.5 rounded-md font-medium text-center"
              >
                Get A Quote
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </nav>
  );
};

// Hero Section
const HeroSection = () => {
  return (
    <section className="relative min-h-screen flex items-center justify-center overflow-hidden pt-20">
      {/* Purple Gradient Background */}
      <div className="absolute inset-0 hero-gradient"></div>
      
      {/* Decorative elements */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute top-20 right-20 w-64 h-64 bg-secondary rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-accent rounded-full blur-3xl"></div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-32 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center"
        >
          <span className="inline-block px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 text-white text-sm font-medium mb-6">
            Imecom Inc. is now Alhambra
          </span>
          
          <h1 className="font-heading text-5xl md:text-7xl font-bold mb-6 text-white">
            Your Trusted{" "}
            <span className="accent-underline">IT Partner</span>
          </h1>
          
          <p className="text-white/80 text-lg md:text-xl max-w-3xl mx-auto mb-10 leading-relaxed">
            Enterprise-grade cloud infrastructure, secure fax solutions, and managed services 
            for businesses worldwide. Leave IT in our hands.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#quote"
              data-testid="hero-get-quote"
              className="btn-primary px-8 py-4 font-semibold text-lg inline-flex items-center justify-center gap-2"
            >
              Get A Quote
              <ArrowRight size={20} />
            </a>
            <a
              href="#services"
              data-testid="hero-explore-services"
              className="px-8 py-4 rounded-full font-semibold text-lg inline-flex items-center justify-center gap-2 bg-white/10 backdrop-blur-sm border border-white/30 text-white hover:bg-white/20 transition-all"
            >
              Explore Services
              <ChevronDown size={20} />
            </a>
          </div>
        </motion.div>

        {/* Stats Row */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.3 }}
          className="grid grid-cols-3 gap-8 mt-20 max-w-2xl mx-auto"
        >
          {[
            { value: "30+", label: "Years Experience" },
            { value: "5000+", label: "Customers" },
            { value: "99.9%", label: "Uptime SLA" },
          ].map((stat, idx) => (
            <div key={idx} className="text-center stat-item" style={{ animationDelay: `${idx * 0.1}s` }}>
              <div className="font-heading text-3xl md:text-4xl font-bold text-white mb-2">
                {stat.value}
              </div>
              <div className="text-white/70 text-sm">{stat.label}</div>
            </div>
          ))}
        </motion.div>
      </div>

      {/* Scroll Indicator */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-10 left-1/2 -translate-x-1/2"
      >
        <motion.div
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
          className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2"
        >
          <div className="w-1 h-2 bg-white rounded-full"></div>
        </motion.div>
      </motion.div>
    </section>
  );
};

// Client Logos Marquee
const ClientLogos = () => {
  const logos = [
    { name: "Toyota", url: "https://logo.clearbit.com/toyota.com?format=png&size=100&greyscale=true" },
    { name: "MetLife", url: "https://logo.clearbit.com/metlife.com?format=png&size=100&greyscale=true" },
    { name: "Johnson & Johnson", url: "https://logo.clearbit.com/jnj.com?format=png&size=100&greyscale=true" },
    { name: "FedEx", url: "https://logo.clearbit.com/fedex.com?format=png&size=100&greyscale=true" },
    { name: "Global Payments", url: "https://logo.clearbit.com/globalpayments.com?format=png&size=100&greyscale=true" },
    { name: "Volkswagen", url: "https://logo.clearbit.com/vw.com?format=png&size=100&greyscale=true" },
  ];

  return (
    <section className="py-16 border-y border-border overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 mb-8">
        <p className="text-text-muted text-center text-sm uppercase tracking-wider">
          Trusted by thousands of customers worldwide
        </p>
      </div>
      <div className="relative">
        <div className="flex marquee-track">
          {[...logos, ...logos].map((logo, idx) => (
            <div
              key={idx}
              className="flex-shrink-0 mx-12 grayscale opacity-50 hover:grayscale-0 hover:opacity-100 transition-all duration-300"
            >
              <img
                src={logo.url}
                alt={logo.name}
                className="h-10 w-auto object-contain"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: Server,
      title: "SDDC and IaaS",
      desc: "Software Defined Data Center services for IaaS, PaaS, and virtual server hosting. Scale your infrastructure on demand.",
      tags: ["Virtual Servers", "Managed Hosting", "Disaster Recovery"],
    },
    {
      icon: ShieldCheck,
      title: "Secure Online Fax",
      desc: "Cloud-based fax service with enterprise-grade security. Send and receive faxes from anywhere, anytime.",
      tags: ["HIPAA Compliant", "Email Integration", "API Access"],
    },
    {
      icon: Database,
      title: "Fax Server",
      desc: "Enterprise fax server with .NET architecture. Complete control over your fax infrastructure.",
      tags: [".NET Architecture", "MFP Integration", "Email to Fax"],
    },
    {
      icon: CloudCog,
      title: "Hybrid Fax Solutions",
      desc: "Combine the best of both worlds. On-premise control with cloud flexibility and backup.",
      tags: ["Flexible Architecture", "Cloud Backup", "Easy Migration"],
    },
  ];

  return (
    <section id="services" className="py-32 relative">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-primary font-medium mb-4 block">Our Services</span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-text-main mb-4">
            Enterprise IT Solutions
          </h2>
          <p className="text-text-muted max-w-2xl mx-auto">
            From cloud infrastructure to secure document transmission, we deliver the
            technology solutions your business needs to thrive.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          {services.map((service, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="bg-surface p-8 rounded-lg card-hover group"
              data-testid={`service-card-${idx}`}
            >
              <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center mb-6 group-hover:bg-primary/20 transition-colors">
                <service.icon className="text-primary" size={24} />
              </div>
              <h3 className="font-heading text-xl font-semibold text-text-main mb-3">
                {service.title}
              </h3>
              <p className="text-text-muted mb-6">{service.desc}</p>
              <div className="flex flex-wrap gap-2">
                {service.tags.map((tag, tagIdx) => (
                  <span
                    key={tagIdx}
                    className="px-3 py-1 bg-surface-highlight text-text-muted text-sm rounded-full"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  return (
    <section id="about" className="py-32 bg-surface">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-primary font-medium mb-4 block">About Us</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-text-main mb-6">
              Since 1991, Powering Digital Transformation
            </h2>
            <p className="text-text-muted text-lg leading-relaxed mb-8">
              Alhambra IT has been at the forefront of enterprise technology for over three decades. 
              We offer complete solutions in Cloud, Information Security, Infrastructure, IP Telephony, 
              and Dedicated Connectivity, supported by our high-capacity proprietary network, 
              24/7 technical teams, and presence in major global datacenters.
            </p>
            <div className="grid sm:grid-cols-2 gap-6">
              {[
                { icon: Building2, text: "Global Datacenter Presence" },
                { icon: Users, text: "24/7 Expert Support" },
                { icon: Zap, text: "High-Capacity Network" },
                { icon: Award, text: "Industry Certifications" },
              ].map((item, idx) => (
                <div key={idx} className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <item.icon className="text-primary" size={20} />
                  </div>
                  <span className="text-text-main">{item.text}</span>
                </div>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="aspect-square rounded-2xl overflow-hidden">
              <img
                src="https://images.unsplash.com/photo-1558494949-ef010cbdcc31?q=80&w=800&auto=format&fit=crop"
                alt="Enterprise Data Center"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-6 -left-6 bg-primary text-primary-foreground p-6 rounded-xl">
              <div className="font-heading text-4xl font-bold">30+</div>
              <div className="text-sm opacity-90">Years of Excellence</div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Security Section
const SecuritySection = () => {
  const certifications = [
    {
      title: "HIPAA Compliant",
      desc: "Multi-layer encryption, strong access control policies, and secure facilities protect your healthcare data.",
    },
    {
      title: "SSAE-16 Type 2",
      desc: "Our data center facilities meet rigorous auditing standards for security and operational excellence.",
    },
    {
      title: "ISO/IEC 27001:2022",
      desc: "Compliant with ISO/IEC 27001:2022 for Information Security Management System ensuring best practices in data protection.",
    },
  ];

  const features = [
    "Multi-layer encryption at rest and in transit",
    "Strong access control policies",
    "Secure SSAE-16 Type 2 data center facilities",
    "99.9% uptime SLA guarantee",
    "24/7 monitoring and support",
    "Regular security audits and penetration testing",
  ];

  return (
    <section id="security" className="py-32">
      <div className="max-w-7xl mx-auto px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <span className="text-primary font-medium mb-4 block">Security & Compliance</span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold text-text-main mb-4">
            Trusted and Secure
          </h2>
          <p className="text-text-muted max-w-2xl mx-auto">
            Alhambra ensures strong network performance and uptime through solid partnerships
            and significant investment in network hardware.
          </p>
        </motion.div>

        {/* Feature Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4 mb-16">
          {features.map((feature, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.05 }}
              className="flex items-center gap-3 bg-surface p-4 rounded-lg"
            >
              <CheckCircle className="text-primary flex-shrink-0" size={20} />
              <span className="text-text-main text-sm">{feature}</span>
            </motion.div>
          ))}
        </div>

        {/* Certifications */}
        <div className="grid md:grid-cols-3 gap-6">
          {certifications.map((cert, idx) => (
            <motion.div
              key={idx}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: idx * 0.1 }}
              className="bg-surface p-8 rounded-lg border border-border hover:border-primary transition-colors text-center"
              data-testid={`certification-${idx}`}
            >
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mx-auto mb-6">
                <ShieldCheck className="text-primary" size={32} />
              </div>
              <h3 className="font-heading text-lg font-semibold text-text-main mb-3">
                {cert.title}
              </h3>
              <p className="text-text-muted text-sm">{cert.desc}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Contact Section with Forms
const ContactSection = () => {
  const [activeTab, setActiveTab] = useState("support");
  const [supportForm, setSupportForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    priority: "normal",
  });
  const [quoteForm, setQuoteForm] = useState({
    company_name: "",
    contact_name: "",
    email: "",
    phone: "",
    services: [],
    message: "",
  });
  const [submitStatus, setSubmitStatus] = useState({ type: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Handle URL hash to switch tabs
  useEffect(() => {
    const handleHashChange = () => {
      if (window.location.hash === "#quote") {
        setActiveTab("quote");
        setSubmitStatus({ type: "", message: "" });
      }
    };
    
    // Check on mount
    handleHashChange();
    
    // Listen for hash changes
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, []);

  const services = [
    "SDDC and IaaS",
    "Secure Online Fax",
    "Fax Server",
    "Hybrid Fax Solutions",
    "Managed Services",
    "Consulting",
  ];

  const handleSupportSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: "", message: "" });

    try {
      await axios.post(`${API}/support-request`, supportForm);
      setSubmitStatus({
        type: "success",
        message: "Support request submitted successfully! A confirmation email has been sent to your inbox. Our team will contact you shortly.",
      });
      setSupportForm({
        name: "",
        email: "",
        phone: "",
        subject: "",
        message: "",
        priority: "normal",
      });
    } catch (error) {
      setSubmitStatus({
        type: "error",
        message: "Failed to submit request. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleQuoteSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus({ type: "", message: "" });

    try {
      await axios.post(`${API}/quote-request`, quoteForm);
      setSubmitStatus({
        type: "success",
        message: "Quote request submitted successfully! A confirmation email has been sent to your inbox. Our sales team will reach out soon.",
      });
      setQuoteForm({
        company_name: "",
        contact_name: "",
        email: "",
        phone: "",
        services: [],
        message: "",
      });
    } catch (error) {
      setSubmitStatus({
        type: "error",
        message: "Failed to submit request. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleServiceToggle = (service) => {
    setQuoteForm((prev) => ({
      ...prev,
      services: prev.services.includes(service)
        ? prev.services.filter((s) => s !== service)
        : [...prev.services, service],
    }));
  };

  return (
    <section id="contact" className="py-32 bg-surface">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
          >
            <span className="text-primary font-medium mb-4 block">Get In Touch</span>
            <h2 className="font-heading text-4xl md:text-5xl font-bold text-text-main mb-6">
              Need immediate assistance?
            </h2>
            <p className="text-text-muted text-lg mb-10">
              Our team of experts is ready to answer your questions and help you find
              the right solution for your business.
            </p>

            <div className="space-y-6 mb-10">
              <a
                href="tel:8003299099"
                data-testid="contact-phone"
                className="flex items-center gap-4 p-4 bg-background rounded-lg hover:bg-surface-highlight transition-colors group"
              >
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <Phone className="text-primary" size={24} />
                </div>
                <div>
                  <div className="text-text-main font-medium">(800) 329-9099</div>
                  <div className="text-text-muted text-sm">Call us anytime</div>
                </div>
              </a>

              <a
                href="mailto:support@us.alhambrait.com"
                data-testid="contact-email"
                className="flex items-center gap-4 p-4 bg-background rounded-lg hover:bg-surface-highlight transition-colors group"
              >
                <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                  <Mail className="text-primary" size={24} />
                </div>
                <div>
                  <div className="text-text-main font-medium">support@us.alhambrait.com</div>
                  <div className="text-text-muted text-sm">Email us for support</div>
                </div>
              </a>
            </div>

            <div className="grid grid-cols-3 gap-6">
              {[
                { value: "24/7", label: "Support Available" },
                { value: "<1hr", label: "Avg Response Time" },
                { value: "98%", label: "Satisfaction Rate" },
              ].map((stat, idx) => (
                <div key={idx} className="text-center">
                  <div className="font-heading text-2xl font-bold text-primary mb-1">
                    {stat.value}
                  </div>
                  <div className="text-text-muted text-xs">{stat.label}</div>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Forms */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            id="quote"
          >
            {/* Tab Buttons */}
            <div className="flex mb-6 bg-background rounded-lg p-1">
              <button
                onClick={() => {
                  setActiveTab("support");
                  setSubmitStatus({ type: "", message: "" });
                }}
                data-testid="tab-support"
                className={`flex-1 py-3 px-4 rounded-md font-medium transition-all ${
                  activeTab === "support"
                    ? "bg-primary text-primary-foreground"
                    : "text-text-muted hover:text-text-main"
                }`}
              >
                <FileText className="inline mr-2" size={18} />
                Support Request
              </button>
              <button
                onClick={() => {
                  setActiveTab("quote");
                  setSubmitStatus({ type: "", message: "" });
                }}
                data-testid="tab-quote"
                className={`flex-1 py-3 px-4 rounded-md font-medium transition-all ${
                  activeTab === "quote"
                    ? "bg-primary text-primary-foreground"
                    : "text-text-muted hover:text-text-main"
                }`}
              >
                <Send className="inline mr-2" size={18} />
                Request Quote
              </button>
            </div>

            {/* Status Message */}
            <AnimatePresence>
              {submitStatus.message && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className={`p-4 rounded-lg mb-6 ${
                    submitStatus.type === "success"
                      ? "bg-green-500/10 border border-green-500/20 text-green-400"
                      : "bg-red-500/10 border border-red-500/20 text-red-400"
                  }`}
                  data-testid="form-status"
                >
                  {submitStatus.message}
                </motion.div>
              )}
            </AnimatePresence>

            {/* Support Form */}
            {activeTab === "support" && (
              <form onSubmit={handleSupportSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-text-muted text-sm mb-2">Name *</label>
                    <input
                      type="text"
                      required
                      value={supportForm.name}
                      onChange={(e) =>
                        setSupportForm({ ...supportForm, name: e.target.value })
                      }
                      data-testid="support-name"
                      className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-main placeholder-text-muted focus:border-primary transition-colors"
                      placeholder="Your name"
                    />
                  </div>
                  <div>
                    <label className="block text-text-muted text-sm mb-2">Email *</label>
                    <input
                      type="email"
                      required
                      value={supportForm.email}
                      onChange={(e) =>
                        setSupportForm({ ...supportForm, email: e.target.value })
                      }
                      data-testid="support-email"
                      className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-main placeholder-text-muted focus:border-primary transition-colors"
                      placeholder="your@email.com"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-text-muted text-sm mb-2">Phone</label>
                    <input
                      type="tel"
                      value={supportForm.phone}
                      onChange={(e) =>
                        setSupportForm({ ...supportForm, phone: e.target.value })
                      }
                      data-testid="support-phone"
                      className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-main placeholder-text-muted focus:border-primary transition-colors"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  <div>
                    <label className="block text-text-muted text-sm mb-2">Priority</label>
                    <select
                      value={supportForm.priority}
                      onChange={(e) =>
                        setSupportForm({ ...supportForm, priority: e.target.value })
                      }
                      data-testid="support-priority"
                      className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-main focus:border-primary transition-colors"
                    >
                      <option value="low">Low</option>
                      <option value="normal">Normal</option>
                      <option value="high">High</option>
                      <option value="urgent">Urgent</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-text-muted text-sm mb-2">Subject *</label>
                  <input
                    type="text"
                    required
                    value={supportForm.subject}
                    onChange={(e) =>
                      setSupportForm({ ...supportForm, subject: e.target.value })
                    }
                    data-testid="support-subject"
                    className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-main placeholder-text-muted focus:border-primary transition-colors"
                    placeholder="Brief description of your issue"
                  />
                </div>

                <div>
                  <label className="block text-text-muted text-sm mb-2">Message *</label>
                  <textarea
                    required
                    rows={4}
                    value={supportForm.message}
                    onChange={(e) =>
                      setSupportForm({ ...supportForm, message: e.target.value })
                    }
                    data-testid="support-message"
                    className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-main placeholder-text-muted focus:border-primary transition-colors resize-none"
                    placeholder="Please describe your support request in detail..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  data-testid="support-submit"
                  className="w-full btn-primary py-4 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "Submitting..." : "Submit Support Request"}
                </button>
              </form>
            )}

            {/* Quote Form */}
            {activeTab === "quote" && (
              <form onSubmit={handleQuoteSubmit} className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-text-muted text-sm mb-2">
                      Company Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={quoteForm.company_name}
                      onChange={(e) =>
                        setQuoteForm({ ...quoteForm, company_name: e.target.value })
                      }
                      data-testid="quote-company"
                      className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-main placeholder-text-muted focus:border-primary transition-colors"
                      placeholder="Your company"
                    />
                  </div>
                  <div>
                    <label className="block text-text-muted text-sm mb-2">
                      Contact Name *
                    </label>
                    <input
                      type="text"
                      required
                      value={quoteForm.contact_name}
                      onChange={(e) =>
                        setQuoteForm({ ...quoteForm, contact_name: e.target.value })
                      }
                      data-testid="quote-contact"
                      className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-main placeholder-text-muted focus:border-primary transition-colors"
                      placeholder="Your name"
                    />
                  </div>
                </div>

                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-text-muted text-sm mb-2">Email *</label>
                    <input
                      type="email"
                      required
                      value={quoteForm.email}
                      onChange={(e) =>
                        setQuoteForm({ ...quoteForm, email: e.target.value })
                      }
                      data-testid="quote-email"
                      className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-main placeholder-text-muted focus:border-primary transition-colors"
                      placeholder="your@email.com"
                    />
                  </div>
                  <div>
                    <label className="block text-text-muted text-sm mb-2">Phone</label>
                    <input
                      type="tel"
                      value={quoteForm.phone}
                      onChange={(e) =>
                        setQuoteForm({ ...quoteForm, phone: e.target.value })
                      }
                      data-testid="quote-phone"
                      className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-main placeholder-text-muted focus:border-primary transition-colors"
                      placeholder="(555) 123-4567"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-text-muted text-sm mb-2">
                    Services Interested In *
                  </label>
                  <div className="grid grid-cols-2 gap-2">
                    {services.map((service) => (
                      <label
                        key={service}
                        className={`flex items-center gap-2 p-3 rounded-lg border cursor-pointer transition-all ${
                          quoteForm.services.includes(service)
                            ? "border-primary bg-primary/10"
                            : "border-border hover:border-border-highlight"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={quoteForm.services.includes(service)}
                          onChange={() => handleServiceToggle(service)}
                          className="hidden"
                          data-testid={`quote-service-${service.toLowerCase().replace(/\s+/g, "-")}`}
                        />
                        <div
                          className={`w-4 h-4 rounded border flex items-center justify-center ${
                            quoteForm.services.includes(service)
                              ? "bg-primary border-primary"
                              : "border-border-highlight"
                          }`}
                        >
                          {quoteForm.services.includes(service) && (
                            <CheckCircle size={12} className="text-white" />
                          )}
                        </div>
                        <span className="text-text-main text-sm">{service}</span>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-text-muted text-sm mb-2">
                    Additional Message
                  </label>
                  <textarea
                    rows={3}
                    value={quoteForm.message}
                    onChange={(e) =>
                      setQuoteForm({ ...quoteForm, message: e.target.value })
                    }
                    data-testid="quote-message"
                    className="w-full bg-background border border-border rounded-lg px-4 py-3 text-text-main placeholder-text-muted focus:border-primary transition-colors resize-none"
                    placeholder="Tell us more about your requirements..."
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || quoteForm.services.length === 0}
                  data-testid="quote-submit"
                  className="w-full btn-primary py-4 rounded-lg font-semibold disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? "Submitting..." : "Request Quote"}
                </button>
              </form>
            )}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="py-16 border-t border-border">
      <div className="max-w-7xl mx-auto px-6">
        <div className="grid md:grid-cols-4 gap-12 mb-12">
          <div className="md:col-span-2">
            <AlhambraLogo className="h-12 mb-6" />
            <p className="text-text-muted mb-6 max-w-md">
              Your trusted IT partner delivering enterprise-grade cloud infrastructure,
              secure fax solutions, and managed services for businesses worldwide.
            </p>
            <div className="flex items-center gap-4">
              <Clock className="text-primary" size={20} />
              <span className="text-text-muted">24/7 Support Available</span>
            </div>
          </div>

          <div>
            <h4 className="font-heading font-semibold text-text-main mb-4">Services</h4>
            <ul className="space-y-3">
              {["SDDC and IaaS", "Secure Online Fax", "Fax Server", "Hybrid Solutions"].map(
                (item) => (
                  <li key={item}>
                    <a href="#services" className="text-text-muted hover:text-text-main transition-colors">
                      {item}
                    </a>
                  </li>
                )
              )}
            </ul>
          </div>

          <div>
            <h4 className="font-heading font-semibold text-text-main mb-4">Contact</h4>
            <ul className="space-y-3">
              <li>
                <a
                  href="tel:8003299099"
                  className="text-text-muted hover:text-text-main transition-colors"
                >
                  (800) 329-9099
                </a>
              </li>
              <li>
                <a
                  href="mailto:support@us.alhambrait.com"
                  className="text-text-muted hover:text-text-main transition-colors"
                >
                  support@us.alhambrait.com
                </a>
              </li>
              <li>
                <a
                  href="mailto:sales@us.alhambrait.com"
                  className="text-text-muted hover:text-text-main transition-colors"
                >
                  sales@us.alhambrait.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="pt-8 border-t border-border flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex flex-col md:flex-row items-center gap-2 md:gap-4">
            <p className="text-text-muted text-sm">
              © {new Date().getFullYear()} Alhambra IT. All rights reserved.
            </p>
            <a href="/privacy" className="text-text-muted text-sm hover:text-primary transition-colors">
              Privacy Policy
            </a>
          </div>
          <div className="flex gap-6">
            {["HIPAA", "SSAE-16", "ISO 27001:2022"].map((cert) => (
              <span key={cert} className="text-text-muted text-xs bg-surface px-3 py-1 rounded">
                {cert}
              </span>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

// Main Home Component
const Home = () => {
  return (
    <div className="noise-bg">
      <Navigation />
      <HeroSection />
      <ServicesSection />
      <AboutSection />
      <SecuritySection />
      <ContactSection />
      <Footer />
    </div>
  );
};

// Privacy Policy Page
const PrivacyPolicy = () => {
  useEffect(() => {
    // Scroll to top on mount
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="noise-bg min-h-screen">
      {/* Simple Nav */}
      <nav className="glass-nav py-4 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 flex items-center justify-between">
          <a href="/" data-testid="privacy-logo-link">
            <AlhambraLogo className="h-12" />
          </a>
          <a
            href="/"
            className="btn-primary px-5 py-2 font-medium text-sm"
          >
            Back to Home
          </a>
        </div>
      </nav>

      {/* Privacy Policy Content */}
      <main className="max-w-4xl mx-auto px-6 py-16">
        <article className="prose prose-lg max-w-none">
          <h1 className="font-heading text-4xl md:text-5xl font-bold text-text-main mb-4">
            Privacy Policy
          </h1>
          
          <div className="text-text-muted mb-8">
            <p><strong>Effective Date:</strong> March 10, 2026</p>
            <p><strong>Last Updated:</strong> March 10, 2026</p>
          </div>

          <p className="text-text-main leading-relaxed mb-8">
            Alhambra IT ("Alhambra," "we," "us," or "our") respects your privacy and is committed to protecting the information you share with us through us.alhambrait.com and any related pages, forms, support tools, communications, and services (collectively, the "Site").
          </p>
          <p className="text-text-main leading-relaxed mb-8">
            This Privacy Policy explains what information we collect, how we use it, when we share it, and the choices you may have regarding your information.
          </p>

          {/* Section 1 */}
          <section className="mb-10">
            <h2 className="font-heading text-2xl font-semibold text-text-main mb-4">1. Information We Collect</h2>
            <p className="text-text-muted mb-4">We may collect information in the following ways:</p>
            
            <h3 className="font-heading text-xl font-medium text-text-main mb-3">Information You Provide to Us</h3>
            <p className="text-text-muted mb-3">When you contact us, request information, open a support request, request remote support, or otherwise interact with us, we may collect information such as:</p>
            <ul className="list-disc pl-6 text-text-muted mb-6 space-y-1">
              <li>Name</li>
              <li>Company name</li>
              <li>Email address</li>
              <li>Phone number</li>
              <li>Job title</li>
              <li>Mailing or billing address</li>
              <li>Information you submit through contact, quote, support, or ticket forms</li>
              <li>Account or service-related information you provide to us</li>
              <li>Communications you send to us</li>
              <li>Any other information you choose to provide</li>
            </ul>

            <h3 className="font-heading text-xl font-medium text-text-main mb-3">Information Collected Automatically</h3>
            <p className="text-text-muted mb-3">When you use the Site, we may automatically collect certain technical and usage information, such as:</p>
            <ul className="list-disc pl-6 text-text-muted mb-6 space-y-1">
              <li>IP address</li>
              <li>Browser type and version</li>
              <li>Device type</li>
              <li>Operating system</li>
              <li>Referring pages</li>
              <li>Pages viewed</li>
              <li>Date and time of visits</li>
              <li>General usage and diagnostic information</li>
            </ul>

            <h3 className="font-heading text-xl font-medium text-text-main mb-3">Information Related to Services</h3>
            <p className="text-text-muted">
              If you engage us for services, support, or product demonstrations, we may receive business contact information and service-related data necessary to provide those services.
            </p>
          </section>

          {/* Section 2 */}
          <section className="mb-10">
            <h2 className="font-heading text-2xl font-semibold text-text-main mb-4">2. How We Use Information</h2>
            <p className="text-text-muted mb-3">We may use collected information to:</p>
            <ul className="list-disc pl-6 text-text-muted space-y-1">
              <li>Provide and operate the Site</li>
              <li>Respond to inquiries and quote requests</li>
              <li>Provide customer service and technical support</li>
              <li>Open, manage, and respond to tickets or service requests</li>
              <li>Provide remote support or other requested services</li>
              <li>Communicate with you about products, services, updates, and support matters</li>
              <li>Improve the Site, our services, and user experience</li>
              <li>Monitor performance, security, and abuse of the Site</li>
              <li>Comply with legal, regulatory, and contractual obligations</li>
              <li>Protect our rights, systems, users, and business operations</li>
            </ul>
            <p className="text-text-muted mt-4 text-sm italic">
              If California privacy law applies to your business, your policy must describe categories of personal information collected and the purposes for which they are used.
            </p>
          </section>

          {/* Section 3 */}
          <section className="mb-10">
            <h2 className="font-heading text-2xl font-semibold text-text-main mb-4">3. Cookies and Similar Technologies</h2>
            <p className="text-text-muted mb-4">
              We may use cookies and similar technologies to help the Site function, maintain session preferences, improve performance, and understand how visitors use the Site.
            </p>
            <p className="text-text-muted">
              You can control cookies through your browser settings. Disabling cookies may affect how some parts of the Site function.
            </p>
          </section>

          {/* Section 4 */}
          <section className="mb-10">
            <h2 className="font-heading text-2xl font-semibold text-text-main mb-4">4. How We Share Information</h2>
            <p className="text-text-muted mb-4"><strong>We do not sell personal information for money.</strong></p>
            <p className="text-text-muted mb-3">We may share information with:</p>
            <ul className="list-disc pl-6 text-text-muted mb-4 space-y-1">
              <li>Hosting and infrastructure providers</li>
              <li>IT, security, and support vendors</li>
              <li>Communications and email service providers</li>
              <li>Customer support, ticketing, and remote-support providers</li>
              <li>Payment processors or billing providers, where applicable</li>
              <li>Professional advisors, auditors, insurers, or legal counsel</li>
              <li>Government, regulators, or law enforcement where required by law</li>
              <li>A successor entity in connection with a merger, acquisition, financing, or sale of assets</li>
            </ul>
            <p className="text-text-muted">
              We may also share information when necessary to detect, prevent, or address fraud, security issues, or misuse of our Site or services.
            </p>
          </section>

          {/* Section 5 */}
          <section className="mb-10">
            <h2 className="font-heading text-2xl font-semibold text-text-main mb-4">5. Healthcare, Fax, and Sensitive Information</h2>
            <p className="text-text-muted mb-4">
              Alhambra offers services related to secure communications and healthcare-oriented workflows. If you use our services in a regulated environment, additional contractual terms, security requirements, and compliance obligations may apply.
            </p>
            <p className="text-text-muted mb-4">
              <strong>Please do not submit medical records, patient data, or other highly sensitive information through general website forms unless we have specifically instructed you to do so through an approved secure method.</strong>
            </p>
            <p className="text-text-muted mb-4">
              Where Alhambra acts as a business associate or service provider for regulated data, our handling of that information may also be governed by separate agreements and applicable law.
            </p>
            <p className="text-text-muted text-sm italic">
              HIPAA applies to protected health information handled by covered entities and business associates, and those roles carry specific obligations beyond a standard website privacy policy.
            </p>
          </section>

          {/* Section 6 */}
          <section className="mb-10">
            <h2 className="font-heading text-2xl font-semibold text-text-main mb-4">6. Data Retention</h2>
            <p className="text-text-muted mb-3">We retain personal information for as long as reasonably necessary to:</p>
            <ul className="list-disc pl-6 text-text-muted space-y-1">
              <li>Fulfill the purposes described in this Privacy Policy</li>
              <li>Provide requested services and support</li>
              <li>Maintain business and legal records</li>
              <li>Enforce agreements</li>
              <li>Resolve disputes</li>
              <li>Meet legal, regulatory, tax, accounting, or compliance requirements</li>
            </ul>
          </section>

          {/* Section 7 */}
          <section className="mb-10">
            <h2 className="font-heading text-2xl font-semibold text-text-main mb-4">7. Data Security</h2>
            <p className="text-text-muted mb-4">
              We use reasonable administrative, technical, and physical safeguards designed to protect personal information. However, no system can be guaranteed to be completely secure, and we cannot guarantee absolute security.
            </p>
            <p className="text-text-muted text-sm italic">
              Any statements about security should accurately reflect real practices. The FTC has repeatedly emphasized that privacy and security representations must be truthful and not misleading.
            </p>
          </section>

          {/* Section 8 */}
          <section className="mb-10">
            <h2 className="font-heading text-2xl font-semibold text-text-main mb-4">8. Your Choices and Rights</h2>
            <p className="text-text-muted mb-3">Depending on your location, you may have privacy rights regarding your personal information, which may include the right to:</p>
            <ul className="list-disc pl-6 text-text-muted mb-4 space-y-1">
              <li>Request access to certain personal information we hold about you</li>
              <li>Request correction of inaccurate information</li>
              <li>Request deletion of certain personal information</li>
              <li>Request information about how we collect, use, or disclose personal information</li>
              <li>Opt out of certain communications</li>
            </ul>
            <p className="text-text-muted">
              To make a privacy request, contact us using the information below. We may need to verify your identity before processing certain requests.
            </p>
          </section>

          {/* Section 9 */}
          <section className="mb-10">
            <h2 className="font-heading text-2xl font-semibold text-text-main mb-4">9. Marketing Communications</h2>
            <p className="text-text-muted">
              If we send marketing emails, you may opt out by using the unsubscribe link in the message or by contacting us directly. Even if you opt out of marketing messages, we may still send service-related or administrative communications.
            </p>
          </section>

          {/* Section 10 */}
          <section className="mb-10">
            <h2 className="font-heading text-2xl font-semibold text-text-main mb-4">10. Third-Party Links and Services</h2>
            <p className="text-text-muted">
              The Site may contain links to third-party websites, platforms, or services. We are not responsible for the privacy practices of those third parties. We encourage you to review their privacy policies separately.
            </p>
          </section>

          {/* Section 11 */}
          <section className="mb-10">
            <h2 className="font-heading text-2xl font-semibold text-text-main mb-4">11. Children's Privacy</h2>
            <p className="text-text-muted">
              The Site is not directed to children under 13, and we do not knowingly collect personal information from children under 13 through the Site. If we learn that we have collected such information, we will take reasonable steps to delete it.
            </p>
          </section>

          {/* Section 12 */}
          <section className="mb-10">
            <h2 className="font-heading text-2xl font-semibold text-text-main mb-4">12. California Privacy Notice</h2>
            <p className="text-text-muted mb-4">
              If you are a California resident, you may have rights under the California Consumer Privacy Act, as amended by the California Privacy Rights Act, including rights to know, delete, correct, and access certain information about how your personal information is handled, subject to exceptions under the law.
            </p>
            <p className="text-text-muted mb-3">Over the past 12 months, we may have collected the following categories of personal information:</p>
            <ul className="list-disc pl-6 text-text-muted mb-4 space-y-1">
              <li>Identifiers, such as name, email address, phone number, IP address, and similar contact information</li>
              <li>Commercial or customer records, such as information relating to services requested or purchased</li>
              <li>Internet or electronic network activity information, such as basic browsing or usage data</li>
              <li>Professional or employment-related information, such as company name, title, or business contact details you provide</li>
              <li>Other information you voluntarily submit in communications, forms, or support requests</li>
            </ul>
            <p className="text-text-muted">
              We collect this information for the business purposes described in this Privacy Policy. To exercise applicable privacy rights, contact us using the contact information below.
            </p>
          </section>

          {/* Section 13 */}
          <section className="mb-10">
            <h2 className="font-heading text-2xl font-semibold text-text-main mb-4">13. Changes to This Privacy Policy</h2>
            <p className="text-text-muted">
              We may update this Privacy Policy from time to time. When we do, we will revise the "Last Updated" date above. Your continued use of the Site after any update becomes effective is subject to the revised Privacy Policy.
            </p>
          </section>

          {/* Section 14 */}
          <section className="mb-10">
            <h2 className="font-heading text-2xl font-semibold text-text-main mb-4">14. Contact Us</h2>
            <p className="text-text-muted mb-4">
              If you have questions about this Privacy Policy or want to submit a privacy request, contact:
            </p>
            <div className="bg-surface p-6 rounded-lg border border-border">
              <p className="text-text-main font-semibold mb-2">Alhambra IT</p>
              <p className="text-text-muted">Website: <a href="https://us.alhambrait.com" className="text-primary hover:underline">us.alhambrait.com</a></p>
              <p className="text-text-muted">Email: <a href="mailto:webmaster@us.alhambrait.com" className="text-primary hover:underline">webmaster@us.alhambrait.com</a></p>
              <p className="text-text-muted">Phone: <a href="tel:8003299099" className="text-primary hover:underline">(800) 329-9099</a></p>
              <p className="text-text-muted">Mailing Address: PO Box 1374, Wolfeboro, NH 03894</p>
            </div>
          </section>

        </article>
      </main>

      {/* Simple Footer */}
      <footer className="py-8 border-t border-border">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <p className="text-text-muted text-sm">
            © {new Date().getFullYear()} Alhambra IT. All rights reserved.
          </p>
          <a href="/" className="text-primary text-sm hover:underline mt-2 inline-block">
            Return to Home
          </a>
        </div>
      </footer>
    </div>
  );
};

function App() {
  return (
    <div className="App bg-background min-h-screen">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/privacy" element={<PrivacyPolicy />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
